/**
 * Minimap — Corner minimap showing zone layout, player position,
 * party members, and mob/NPC markers.
 *
 * Reads platform data from MapSystem via ServiceRegistry. One uniform scale;
 * wide maps scroll with the player, small maps show whole (minimapView.ts).
 * The +/− buttons step the zoom from a close-up to the whole level; the
 * choice is remembered in localStorage.
 * Updates player position every frame via polling.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { useRenderCount } from '../../diagnostics/useRenderCount';
import { COLORS, ACCENT, RADII } from '../theme';
import { MAP_MARKERS, npcMapMarker } from './minimapMarkers';
import {
  computeMinimapView, minimapPoint, MINIMAP_ZOOM_STEPS, minimapZoomLabel,
  loadMinimapZoom, saveMinimapZoom, stepMinimapZoom,
} from './minimapView';

interface MinimapProps {
  width?: number;
  height?: number;
}

interface MinimapEntity {
  x: number;
  y: number;
  color: string;
  type: 'player' | 'other' | 'mob' | 'npc' | 'teleporter';
}

/**
 * Equality function for React.memo (F.4, 2026-05-13).
 *
 * Telemetry baseline (`vqa-bot-telemetry`) captured Minimap at
 * 17 renders/sec — bimodal frame distribution showed 44 spikes >33ms
 * per 10s correlating with that churn. The component's only props
 * are width/height primitives; nearly every render is a parent re-
 * render that doesn't change the props.
 *
 * Memoization with shallow primitive compare drops the parent-driven
 * renders to 0; the canvas draw cadence is independent (200ms setInterval
 * inside useEffect — unaffected by reconciliation skips).
 */
function minimapEqual(prev: MinimapProps, next: MinimapProps): boolean {
  return prev.width === next.width && prev.height === next.height;
}

const MinimapInner: React.FC<MinimapProps> = ({ width = 180, height = 140 }) => {
  useRenderCount('Minimap');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(true);
  const [zoom, setZoom] = useState(loadMinimapZoom);
  const changeZoom = useCallback((delta: number) => {
    setZoom(current => {
      const next = stepMinimapZoom(current, delta);
      saveMinimapZoom(next);
      return next;
    });
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const registry = ServiceRegistry.getInstance();

    // Get map data
    let platforms: any[] = [];
    let mapWidth = 2000;
    let mapHeight = 1200;
    let playerX = 0;
    let playerY = 0;
    const entities: MinimapEntity[] = [];

    try {
      if (registry.has('EntitySystem')) {
        const entitySystem = registry.get('EntitySystem');
        const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
        if (player?.position) {
          playerX = player.position.x;
          playerY = player.position.y;
        }
      }

      const mapSystem = registry.has('MapSystem') ? registry.get('MapSystem') : null;
      const level = mapSystem?.getCurrentLevel?.();
      if (level) {
        platforms = level.platforms || [];
        mapWidth = level.width || 2000;
        mapHeight = level.height || 1200;
      }

      // Get NPCs/mobs from game state
      const nm = registry.has('NetworkManager') ? registry.get('NetworkManager') : null;
      const room = nm?.getRoom?.();
      if (room?.state) {
        room.state.npcs?.forEach((npc: any) => {
          entities.push({
            x: npc.x, y: npc.y,
            ...npcMapMarker(npc),
          });
        });

        room.state.players?.forEach((p: any, sid: string) => {
          if (sid !== room.sessionId) {
            entities.push({ x: p.x, y: p.y, color: MAP_MARKERS.adventurer, type: 'other' });
          }
        });
      }
    } catch { /* non-critical */ }

    // One scale for both axes, windowed around the player on wide maps (plan 13 §3).
    const view = computeMinimapView({
      width, height, mapWidth, mapHeight, playerX, playerY, viewWidth: MINIMAP_ZOOM_STEPS[zoom],
    });
    const k = view.scale;
    const at = (x: number, y: number) => minimapPoint(view, x, y);

    // Background (the void outside a letterboxed whole map stays darker)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, width, height);
    const mapTopLeft = at(0, 0);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(mapTopLeft.x, mapTopLeft.y, mapWidth * k, mapHeight * k);

    // Platforms
    ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
    for (const p of platforms) {
      const pos = at(p.x, p.y);
      if (p.type === 'teleporter') {
        if (p.concealed || p.teleporterConfig?.concealed) continue;
        ctx.fillStyle = MAP_MARKERS.portal;
        ctx.fillRect(pos.x, pos.y, Math.max(p.width * k, 3), Math.max(p.height * k, 3));
        ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
      } else {
        ctx.fillRect(pos.x, pos.y, Math.max(p.width * k, 1), Math.max(p.height * k, 1));
      }
    }

    // Mobs, NPCs, other players
    for (const e of entities) {
      ctx.fillStyle = e.color;
      const size = e.type === 'mob' ? 3 : 4;
      const pos = at(e.x, e.y);
      ctx.fillRect(pos.x - size / 2, pos.y - size / 2, size, size);
    }

    // Player (white dot, larger)
    const you = at(playerX, playerY);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(you.x, you.y, 3, 0, Math.PI * 2);
    ctx.fill();

    // Player direction indicator
    ctx.fillStyle = '#ffdd44';
    ctx.beginPath();
    ctx.arc(you.x, you.y, 1.5, 0, Math.PI * 2);
    ctx.fill();

    // Border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, width, height);
  }, [width, height, zoom]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(draw, 200); // 5 FPS for minimap
    draw();
    return () => clearInterval(interval);
  }, [draw, visible]);

  if (!visible) {
    return (
      <button
        type="button"
        data-hud-surface="minimap"
        onClick={() => setVisible(true)}
        style={{
          position: 'fixed', top: 10, right: 10, zIndex: 1000, pointerEvents: 'auto',
          background: COLORS.surfaceSunken, border: `1px solid ${ACCENT.brass}`,
          borderRadius: RADII.md, color: COLORS.textSecondary, padding: '4px 8px',
          fontSize: '11px', cursor: 'pointer',
        }}
      >
        Map
      </button>
    );
  }

  return (
    // pointerEvents: the overlay wrapper above is pointer-events:none, so a HUD
    // surface opts back in or its buttons (Hide, zoom) never receive a click.
    <div data-hud-surface="minimap" style={{
      position: 'fixed', top: 10, right: 10, zIndex: 1000, pointerEvents: 'auto',
      borderRadius: '6px', overflow: 'hidden',
      // Brass fitting frame (HUD-restyle §5) — frame treatment only; the canvas
      // content is untouched this arc.
      border: `1px solid ${ACCENT.brass}`,
      boxShadow: '0 2px 10px rgba(0,0,0,0.55)',
    }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ display: 'block', cursor: 'pointer' }}
        onClick={() => setVisible(false)}
        title="Click to hide minimap"
        role="img"
        aria-label="Zone map: white is you, gold is an NPC, red is an enemy, green is another adventurer, purple is a portal"
      />
      <div aria-label="Map legend" style={{ background: COLORS.surfaceSunken, color: COLORS.textSecondary,
        display: 'flex', flexWrap: 'wrap', gap: '4px 8px', padding: '6px 8px', width, boxSizing: 'border-box', fontSize: 9 }}>
        {([['npc', 'NPC'], ['enemy', 'Enemy'], ['adventurer', 'Hero'], ['portal', 'Portal']] as const).map(([key, label]) =>
          <span key={key}><span aria-hidden="true" style={{ color: MAP_MARKERS[key] }}>■ </span>{label}</span>)}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          <button type="button" onClick={() => changeZoom(-1)} disabled={zoom === 0}
            aria-label="Zoom map in" title={`Zoom in (${minimapZoomLabel(zoom)} across)`} style={zoomButtonStyle(zoom === 0)}>+</button>
          <button type="button" onClick={() => changeZoom(1)} disabled={zoom === MINIMAP_ZOOM_STEPS.length - 1}
            aria-label="Zoom map out" title={`Zoom out (${minimapZoomLabel(zoom)} across)`}
            style={zoomButtonStyle(zoom === MINIMAP_ZOOM_STEPS.length - 1)}>−</button>
          <button type="button" onClick={() => setVisible(false)} aria-label="Hide map"
            style={{ color: COLORS.textSecondary, background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 9 }}>Hide</button>
        </span>
      </div>
    </div>
  );
};

function zoomButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    color: COLORS.textSecondary, background: 'none', border: `1px solid ${ACCENT.brass}`, borderRadius: RADII.sm,
    padding: '0 4px', lineHeight: '11px', fontSize: 11, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
  };
}

export const Minimap = React.memo(MinimapInner, minimapEqual);
Minimap.displayName = 'Minimap';
